import React from "react";
import { Dialog, DialogTitle, DialogContent, Stack } from "@mui/material";
import { MedicareBaseRateType } from "../../../../../services/ConfigurationService/types/centerType";
import { useMedicareBaseRateForm } from "./hooks";
import {
    YearCbsaFields,
    RateFieldsRow,
    AdditionalFieldsRow,
    DialogActionButtons
} from "./components";

interface AddEditMedicareBaseRateDialogProps {
    open: boolean;
    onClose: () => void;
    onSave: (rate: MedicareBaseRateType) => void;
    onDelete?: (rate: MedicareBaseRateType) => void;
    editingRate?: MedicareBaseRateType | null;
    existingRates?: MedicareBaseRateType[];
}

export function AddEditMedicareBaseRateDialog({
    open,
    onClose,
    onSave,
    onDelete,
    editingRate,
    existingRates = []
}: AddEditMedicareBaseRateDialogProps) {
    const {
        form,
        errors,
        isEditMode,
        handleChange,
        validateForm,
        resetForm
    } = useMedicareBaseRateForm({
        editingRate,
        existingRates,
        open
    });

    const handleSave = () => {
        if (validateForm()) {
            // Ensure we have an ID for new records
            const rateToSave = {
                ...form,
                id: form.id || crypto.randomUUID() // Generate ID if it's empty
            };

            console.log('Saving Medicare Base Rate:', rateToSave); // Debug log
            onSave(rateToSave);
            onClose();
        }
    };

    const handleDelete = () => {
        if (editingRate && onDelete) {
            onDelete(editingRate);
            onClose();
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {isEditMode ? 'Edit Medicare Base Rate' : 'Add Medicare Base Rate'}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={3} mt={1}>
                    <YearCbsaFields
                        year={form.year}
                        cbsa={form.cbsa}
                        yearError={errors.year}
                        cbsaError={errors.cbsa}
                        onChange={handleChange}
                    />

                    <RateFieldsRow
                        wageIndex={form.wageIndex}
                        lrop={form.lrop}
                        nlop={form.nlop}
                        onChange={handleChange}
                    />

                    <AdditionalFieldsRow
                        federalCapitalWeight={form.federalCapitalWeight}
                        geographicAdjustmentFactor={form.geographicAdjustmentFactor}
                        inpatientHospitalDeductible={form.inpatientHospitalDeductible}
                        onChange={handleChange}
                    />
                </Stack>
            </DialogContent>

            <DialogActionButtons
                isEditMode={isEditMode}
                isSubmitDisabled={!form.year || !form.cbsa}
                onDelete={onDelete ? handleDelete : undefined}
                onCancel={handleClose}
                onSave={handleSave}
            />
        </Dialog>
    );
}
